import * as React from 'react';
import {
  type ControllerRenderProps,
  type FieldError,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

/**
 * Context shared between the `FormField` root and its subcomponents. The root
 * renders an RHF `Controller` and publishes the controlled `field` plus the
 * generated a11y ids and current error, so subcomponents auto-wire to the
 * field without re-specifying its name.
 */
export interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  field: ControllerRenderProps<TFieldValues, TName>;
  error: FieldError | undefined;
  formItemId: string;
  formDescriptionId: string;
  formMessageId: string;
}

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

export function FormFieldProvider({
  value,
  children,
}: {
  value: FormFieldContextValue;
  children: React.ReactNode;
}) {
  return <FormFieldContext.Provider value={value}>{children}</FormFieldContext.Provider>;
}

/** Read the enclosing FormField context. Throws if used outside a `FormField`. */
export function useFormFieldContext(): FormFieldContextValue {
  const ctx = React.useContext(FormFieldContext);
  if (!ctx) {
    throw new Error('FormField subcomponents must be used within a <FormField>.');
  }
  return ctx;
}
